import { useCallback, useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import {
  Building2,
  FileChartColumn,
  Box as Courses,
  Settings,
  LucideUserCircle2,
  LucideUsers,
  MapPin,
  ArrowLeft,
  Receipt,
} from 'lucide-react';
import { t } from '@lingui/core/macro';

import type { MenuItemsType } from '@duke-hq/ui/components/SubSidebar/MenuItems';
import { SidebarLayout } from '@duke-hq/ui/components/SubSidebar/SidebarLayout';
import { useSettings } from '@duke-hq/app-admin-web/contexts/SettingsContext';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { withPermission } from '../withPermission';

export const ProvidersLayout = withPermission('canViewProviders', () => {
  const { providerId } = useParams<{ providerId: string }>();
  const { setProvider } = useSettings();

  // Fetch provider data when providerId is available
  const { data: provider, isLoading } = trpcAdmin.providers.byId.useQuery(
    parseInt(providerId ?? '0', 10),
    { enabled: !!providerId },
  );

  // Set provider in settings context when available
  useEffect(() => {
    if (!isLoading && provider) {
      setProvider(provider);
    }
  }, [isLoading, provider, setProvider]);

  // Determine which menu items to show based on current route
  const getMenuItems = useCallback((): MenuItemsType[] => {
    if (providerId) {
      // Show provider-specific menu items with back navigation
      return [
        {
          label: t`All Providers`,
          link: `/providers`,
          icon: ArrowLeft,
          variant: 'link',
        },
        {
          label: t`Settings`,
          link: `/providers/${providerId}/settings`,
          icon: Settings,
        },
        {
          label: t`Courses`,
          link: `/providers/${providerId}/courses`,
          icon: Courses,
        },
        {
          label: t`Participants`,
          link: `/providers/${providerId}/students`,
          icon: LucideUserCircle2,
        },
        {
          label: t`Consultants`,
          link: `/providers/${providerId}/consultants`,
          icon: LucideUsers,
        },
        {
          label: t`Locations`,
          link: `/providers/${providerId}/locations`,
          icon: MapPin,
        },
        {
          label: t`Billing Rules`,
          link: `/providers/${providerId}/billing-rules`,
          icon: Receipt,
        },
      ];
    } else {
      // Show top-level providers menu items
      return [
        {
          label: t`Providers`,
          link: `/providers`,
          icon: Building2,
        },
        {
          label: t`Reports`,
          link: `/providers/reports`,
          icon: FileChartColumn,
        },
      ];
    }
  }, [providerId]);

  // Get sidebar label based on current context
  const getSidebarLabel = useCallback((): string => {
    if (providerId && provider) {
      return provider.name;
    }
    return t`Providers`;
  }, [providerId, provider]);

  return (
    <SidebarLayout items={getMenuItems()} label={getSidebarLabel()}>
      <Outlet />
    </SidebarLayout>
  );
});
